import * as React from "react";
import * as _ from "lodash";
import { connect } from "react-redux";
import { LinkContainer } from "react-router-bootstrap";
import { NavItem } from "react-bootstrap";
import { IPatientDetail } from "@curefit/care-common";
import { AppointmentsActions } from "../actions/AppointmentsActions";
import { IAppState } from "../models/IAppState"
import IDoctorProfile from "../models/IDoctorProfile"
import { AppMessages, APP_TENANTS, ORION_IMG_URL, QuestionType } from "../utils/AppConstants";
import { OngoingConsultationActions } from "../actions/OngoingConsultationActions";
import { IAccessToken } from "../models/ICommunication";
import { ITwilioContext } from "../models/ITwilioContext";
import { default as AppUtils, LOADING_STATE } from "../utils/AppUtils";
import LazyLoaderComponent from "../components/LazyLoaderComponent";
import { TwilioActions } from "../actions/TwilioActions";
import { PatientCompleteInfoActions } from "../actions/PatientCompleteInfoActions";
import DoctorPatientCard from "../components/patient/DoctorPatientCard";
import SearchForm from "../components/common/SearchForm";
import { ApiManager } from "../api/ApiManager";
import { RouteUtils } from "../routes";
import ToggleButtonList from "../components/ToggleButtonList";
import IStaticDataValue from "../../../common/models/IStaticDataValue";
import { EventNames } from "../utils/EventTrackerConstants";
import { CONVERSE_SDK_EVENTS, eventDispatcher } from "@curefit/converse-chat-sdk";
import { WhatsAppPatientInfo } from "../components/WhatsAppChatComponent";
import { WhatsAppChatActions } from "../actions/WhatsAppChatActions";

export interface AllPatientsSearchPageProps {
    doctorProfile: IDoctorProfile,
    chatAccessToken: IAccessToken[],
    twilioContext: ITwilioContext,
    fetchChatContext: (appointmentId: number, commUserId: number) => null,
    updateCurrentChannelWithPatient: (channelSid: string, patient: IPatientDetail, identity: string) => void,
    updateWhatsAppChatContext: (patientInfo: WhatsAppPatientInfo) => void,
    closeWhatsAppChatContext: () => void,
    fetchDoctorPatients: (doctorId: any, query: any) => Promise<any>
    appTenant: string,
}

export interface DoctorPatient {
    doctorId: number;
    patientDetail: IPatientDetail;
    lastCompletedConsultation?: number;
    twilioModeId: string;
    lastChatTime?: number;
    hasUpcoming: boolean;
    hasInDraft: boolean;
    isChatExpired: boolean;
    doctorMemberIdentity: string;
}

export interface DoctorPatientsSearchResponse {
    content: DoctorPatient[];
    last: boolean;
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    sort?: any;
    first: boolean;
    numberOfElements: number;
}

export interface AllPatientsSearchPageState {
    chatChannelName: string,
    activeAppointment: number,
    channelSid: string,
    activePatientName: string,
    appointmentsLoadingState: LOADING_STATE,
    doctorPatients: DoctorPatient[],
    currentDoctorPatientsPage: number,
    endOfDoctorPatientsPage: boolean,
    doctorPatientsSortOrder: string,
    patientNameContains: string,
    activeMostRecentChat: boolean,
    selectedSearchToggle: string,
}

export const DoctorPatientsPageSize = 25;

const PATIENT_SEARCH_TOGGLE_LIST: IStaticDataValue[] = [
    { displayValue: "Patients Name", internalValue: "name" },
    { displayValue: "Patient UHID", internalValue: "uhid" }
]

class AllPatientsSearchPage extends React.Component<AllPatientsSearchPageProps, AllPatientsSearchPageState> {
    private loadStartTime: number = AppUtils.getCurrentTime()
    constructor(props: AllPatientsSearchPageProps) {
        super(props);
        this.state = {
            chatChannelName: null,
            activeAppointment: null,
            channelSid: null,
            activePatientName: null,
            appointmentsLoadingState: LOADING_STATE.NOT_INITIALIZED,
            doctorPatients: null,
            currentDoctorPatientsPage: 0,
            endOfDoctorPatientsPage: false,
            doctorPatientsSortOrder: "CONSULTATION",
            patientNameContains: "",
            activeMostRecentChat: false,
            selectedSearchToggle: "name"
        };
    };

    componentDidMount() {
        const doctorId = this.props.doctorProfile.id
        const query: any = { pageNo: 0, pageSize: DoctorPatientsPageSize, sortOrder: this.state.doctorPatientsSortOrder, patientNameContains: this.state.patientNameContains }
        this.props.fetchDoctorPatients(doctorId, query)
            .then((doctorPatientSearchResponse: DoctorPatientsSearchResponse) => {
                AppUtils.updatePageLoadEvent(EventNames.My_Patients, "", this.loadStartTime)
                this.setState({
                    doctorPatients: doctorPatientSearchResponse.content
                }
                )
            })
            .catch(
                () => {
                    AppUtils.updatePageLoadEvent(EventNames.My_Patients, "", this.loadStartTime)
                    console.log(`unable to fetch doctor patients for doctorId: ${doctorId} with query : ${query}`)
                }
            )
    }

    setChatContext = (channelSid: string, patient: IPatientDetail, identity: string) => {
        this.props.updateCurrentChannelWithPatient(channelSid, patient, identity);
    }
    setWhatsAppContext = (patientInfo: WhatsAppPatientInfo) => {
        this.props.closeWhatsAppChatContext();
        setTimeout(() => this.props.updateWhatsAppChatContext(patientInfo), 300);
    }

    searchPatientByUHID = (uhid: string): Promise<any> => {
        return ApiManager.getSubUserServiceApis().searchPatientByUHID(uhid)
    }

    handleSearchByUhid = async (uhid: string) => {
        if (uhid.length === 0) return
        try {
            if (!uhid.startsWith("CF")) {
                uhid = "CF" + uhid
            }
            const isTransformCoach = this.props.doctorProfile && this.props.doctorProfile.resourceServiceMapping && AppUtils.checkForTransformCoach(this.props.doctorProfile.resourceServiceMapping);
            const patient: any = await this.searchPatientByUHID(uhid);
            const patientUrl = isTransformCoach ? AppUtils.getPatienTransfomrUrl(patient.id) : AppUtils.getPatientConsultationsUrl(patient.id);
            RouteUtils.navigateToUrl(patientUrl)
        }
        catch (e) {
            alert("Patient not found")
        }

    }

    handleSearch = (value: string) => {
        if (value.toString().length < 2 && value.toString().length !== 0) {
            alert("please enter minimum 2 characters")
            return
        }
        if (this.state.selectedSearchToggle === "uhid") {
            this.handleSearchByUhid(value)
        } else {
            this.setState(
                {
                    appointmentsLoadingState: LOADING_STATE.LOADING,
                    patientNameContains: value,
                    doctorPatients: null
                },
                () => this.props.fetchDoctorPatients(this.props.doctorProfile.id, { pageNo: 0, pageSize: DoctorPatientsPageSize, patientNameContains: this.state.patientNameContains, sortOrder: this.state.doctorPatientsSortOrder })
                    .then((doctorPatientSearchResponse: DoctorPatientsSearchResponse) =>
                        this.setState({
                            doctorPatients: doctorPatientSearchResponse.content, appointmentsLoadingState: LOADING_STATE.LOADED,
                        }))
            )
        }
    }

    fetchSortedDoctorPatients = () => {
        event.preventDefault();
        const doctorPatientsSortOrder = this.state.activeMostRecentChat ? "CONSULTATION" : "CHAT";
        this.setState(
            {
                appointmentsLoadingState: LOADING_STATE.LOADING,
                activeMostRecentChat: !this.state.activeMostRecentChat,
                doctorPatientsSortOrder
            },
            () => this.props.fetchDoctorPatients(this.props.doctorProfile.id, { pageNo: 0, pageSize: DoctorPatientsPageSize, patientNameContains: this.state.patientNameContains, sortOrder: this.state.doctorPatientsSortOrder })
                .then((doctorPatientSearchResponse: DoctorPatientsSearchResponse) =>
                    this.setState({ doctorPatients: doctorPatientSearchResponse.content, appointmentsLoadingState: LOADING_STATE.LOADED }))
        )
    }

    loadMoreDoctorPatients = () => {
        if (!this.state.endOfDoctorPatientsPage && this.state.appointmentsLoadingState !== LOADING_STATE.LOADING) {
            let nextPageNo = this.state.currentDoctorPatientsPage + 1;
            this.setState({
                appointmentsLoadingState: LOADING_STATE.LOADING
            }, () => {
                this.props.fetchDoctorPatients(this.props.doctorProfile.id, { pageNo: nextPageNo, pageSize: DoctorPatientsPageSize, sortOrder: this.state.doctorPatientsSortOrder, patientNameContains: this.state.patientNameContains })
                    .then((doctorPatientSearchResponse: DoctorPatientsSearchResponse) => {
                        if (!_.isEmpty(doctorPatientSearchResponse.content)) {
                            const doctorPatients = doctorPatientSearchResponse.content
                            this.setState({
                                currentDoctorPatientsPage: nextPageNo,
                                doctorPatients: this.state.doctorPatients.concat(doctorPatients),
                                appointmentsLoadingState: LOADING_STATE.LOADED
                            })
                        } else {
                            this.setState({
                                endOfDoctorPatientsPage: true,
                                appointmentsLoadingState: LOADING_STATE.LOADED
                            })
                        }
                    })
            })
        }
    }

    findUnreadCountForPatient = (patientId: number): number => {
        let unreadMessageInfoList = this.props.twilioContext.unreadMessageInfo;
        let index = unreadMessageInfoList.map(item => item.patient.id).indexOf(patientId);
        let unreadCount = null;
        if (index !== -1) unreadCount = unreadMessageInfoList[index].twilioUnreadMessageCountView.unreadMessageCount;
        return unreadCount;
    }

    handleSearchToggleClick = (selectedItem: string) => {
        if (selectedItem) {
            this.setState({
                selectedSearchToggle: selectedItem
            })
        }
    }

    render() {
        const { doctorProfile } = this.props;
        let myInstance = this;
        let endOfList = this.state.endOfDoctorPatientsPage;
        let doctorPatients = this.state.doctorPatients;
        const mostRecentChatButtonCls = this.state.activeMostRecentChat ? "active" : "";
        const searchInputPlaceholder = this.state.selectedSearchToggle === "name" ? "Search by Patient's Name" : "Search by UHID";
        const isTransformCoach = doctorProfile && doctorProfile.resourceServiceMapping && AppUtils.checkForTransformCoach(doctorProfile.resourceServiceMapping);
        let useWhatsAppChat = (this.props.appTenant === APP_TENANTS.SUGARFIT && this.props.doctorProfile.type === "LIFESTYLE_COACH")
        return (
            <div className="my-patients-page all-patients-page">
                <div className="dummy-background-div"></div>
                <div className={"search-container"}>
                    <ToggleButtonList listItems={PATIENT_SEARCH_TOGGLE_LIST} selectedItems={[this.state.selectedSearchToggle]} onSelectionChange={this.handleSearchToggleClick} type={QuestionType.BOOLEAN_TOGGLE} />
                    <SearchForm
                        showClearButton={false}
                        showSearchButton={false}
                        handleSearch={(value: any) => myInstance.handleSearch(value)}
                        inputPlaceHolder={searchInputPlaceholder}
                        formClassName={"doctor-patients-search-form"}
                    >
                    </SearchForm>
                </div>
                {
                    doctorPatients !== null ?
                        <div className="consultations-table">
                            <div className="consultation-header">
                                <div className="brandon-bold">Patient</div>
                                <div className="brandon-bold">Last Consult</div>
                                {!useWhatsAppChat ?
                                    <div className="brandon-bold">{"Chat   "}
                                        {!AppUtils.getIsMobile() && (
                                            <button onClick={() => myInstance.fetchSortedDoctorPatients()} className={`yesOrNoButton focus ${mostRecentChatButtonCls}`}>Most Recent</button>
                                        )}
                                    </div> :
                                    <div className="brandon-bold">WhatsApp</div>
                                }
                            </div>
                            {doctorPatients.length > 0 ?
                                <React.Fragment>
                                    <LazyLoaderComponent reachedEndTrigger={this.loadMoreDoctorPatients}>
                                        <div className="consultation-body">
                                            {
                                                _.map(doctorPatients, (doctorPatient: DoctorPatient) => {
                                                    let patientUrl = isTransformCoach ? AppUtils.getPatienTransfomrUrl(doctorPatient.patientDetail.id) : AppUtils.getPatientConsultationsUrl(doctorPatient.patientDetail.id)
                                                    const unreadCount = this.findUnreadCountForPatient(doctorPatient.patientDetail.id);
                                                    return (
                                                        <LinkContainer to={patientUrl} key={`my-patients-${doctorPatient.patientDetail.id}`}>
                                                            <NavItem title="Completed" >
                                                                <DoctorPatientCard doctorPatient={doctorPatient}
                                                                    openChat={myInstance.setChatContext}
                                                                    unreadCount={unreadCount}
                                                                    openWhatsAppChat={myInstance.setWhatsAppContext}
                                                                    useWhatsAppChat={useWhatsAppChat}
                                                                />
                                                            </NavItem>
                                                        </LinkContainer>
                                                    )
                                                })
                                            }
                                        </div>
                                    </LazyLoaderComponent>
                                    {
                                        this.state.appointmentsLoadingState === LOADING_STATE.LOADING &&
                                        <div className={"loading-appointments"}>
                                            <span>
                                                <img src={ORION_IMG_URL + "loader/rolling-loader.gif"} />
                                            </span>
                                        </div>
                                    }
                                    {
                                        endOfList &&
                                        <div className={"end-of-consultations-list"}>
                                            <div style={{ marginTop: "-10px" }}><span>{AppMessages.MY_PATIENTS_PAGE_END_OF_RESULTS}</span></div>
                                        </div>
                                    }

                                </React.Fragment> : <div className="no-patient-found">No patient found.</div>
                            }

                        </div> : <div className="central-loader my-patients-loader">
                            <img src={ORION_IMG_URL + "loader/curefit-loader.gif"} />
                        </div>
                }
            </div>
        )
    }
}

function mapStateToProps(state: IAppState): any {
    return {
        doctorProfile: state.doctorProfile,
        chatAccessToken: state.chatAndVideoAccessTokens.chatAccessToken,
        twilioContext: state.twilioContext,
        appTenant: state.accountData.appTenant
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        updateCurrentChannelWithPatient: (channelSid: string, patient: IPatientDetail, identity: string) => {
            return dispatch(TwilioActions.updateCurrentChannelWithPatient(channelSid, patient, identity));
        },
        closeWhatsAppChatContext: () => {
            return dispatch(WhatsAppChatActions.closeWhatsAppChat());
        },
        updateWhatsAppChatContext: (patientInfo: WhatsAppPatientInfo) => {
            return dispatch(WhatsAppChatActions.updateWhatsAppPatientInfo(patientInfo));
        },
        fetchChatContext: (appointmentId: number, commUserId: number) => {
            OngoingConsultationActions.fetchChatContext(dispatch, appointmentId, commUserId);
        },
        fetchPaginatedDoctorAppointments: (doctorId: number, pageNo: number, pageSize: number) => {
            return AppointmentsActions.fetchPaginatedDoctorAppointments(dispatch, doctorId, pageNo, pageSize)
        },
        fetchDoctorPatients: (doctorId: number, query: any) => {
            return PatientCompleteInfoActions.searchDoctorPatients(doctorId, query)
        }
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(AllPatientsSearchPage);